import React, { ReactNode } from 'react';
import InputMask, { Props as InputMaskProps } from 'react-input-mask';
import makeStyles from '@mui/styles/makeStyles';
import { InputAdornment, TextField, TextFieldProps } from '@mui/material';
import { RenderWithCondition } from '../shared/Conditional/RenderWithCondition';

interface FormInputProps<FormatterValue = string> extends Omit<TextFieldProps, 'onChange'> {
  mask?: InputMaskProps['mask'];
  startIcon?: ReactNode;
  dataTestId?: string;
  onChange?: (value: FormatterValue) => void;
  formatter?: (value: string) => FormatterValue | null;
}

export const FormInput = <FormatterValue extends string | number = string>({
  mask,
  error,
  onChange,
  formatter,
  startIcon,
  helperText,
  dataTestId,
  value = '',
  ...other
}: FormInputProps<FormatterValue>) => {
  const classes = useStyles();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value: string = event.target.value;

    if (onChange) {
      formatter ? onChange(formatter(value) as FormatterValue) : onChange(value as FormatterValue);
    }
  };

  let inputProps = other?.slotProps?.input ?? {};

  if (startIcon) {
    inputProps = {
      ...inputProps,
      startAdornment: (
        <InputAdornment position="start" className={classes.startAdornment}>
          {startIcon}
        </InputAdornment>
      ),
    };
  }

  const commonProps: TextFieldProps = {
    error,
    size: 'small',
    fullWidth: true,
    variant: 'standard',
    value: value ?? '',
    onChange: handleChange,
    helperText: error ? helperText : '',
    ...other,
    slotProps: {
      input: inputProps,
    },
    inputProps: {
      ...(other as any)?.inputProps,
      ...(dataTestId ? { 'data-testid': dataTestId } : {}),
    },
  };

  return (
    <RenderWithCondition condition={!!mask} fallBack={<TextField {...commonProps} />}>
      <InputMask mask={mask!} {...(commonProps as any)}>
        {renderPros => <TextField disabled={commonProps.disabled} {...renderPros} />}
      </InputMask>
    </RenderWithCondition>
  );
};

const useStyles = makeStyles({
  startAdornment: {
    margin: '0 0 3px 0',

    '& .MuiSvgIcon-root': {
      fontSize: '1.18rem',
      margin: 0,
    },
  },
});
